// 참가자 한 명의 라운드별 스케줄(파트너/상대/결과)을 그리는 빌더.
//
// 일정 상세 화면의 참가자 칩 탭, 가용시간 시트, 팀전 멤버 칩 탭에서
// 동일하게 쓰인다. closure 의존이 없어서 가장 먼저 빼냈다.

import { matchPlayerName } from "../matches";
import { formatTimeShort } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";

interface RoundLike {
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
}

interface ParticipantLike {
  event_participant_id: string;
}

interface MatchLike {
  match_type?: string;
  round_number?: number | null;
  court_label?: string | null;
  player_ids?: string[];
  result_status?: string;
  winner_side?: string;
  sides?: Array<{
    side_number?: number;
    player_ids?: string[];
    players?: any[];
  }>;
}

const TYPE_LABELS: Record<string, string> = {
  men_doubles: "남복",
  women_doubles: "여복",
  mixed_doubles: "혼복",
  exceptional_mix: "잡복",
};

function resolveResult(match: MatchLike, pid: string): string {
  if (!match.result_status || match.result_status === "empty") return "";
  if (match.winner_side === "draw") return "무";
  const mySide = (match.sides || []).find((s) => (s.player_ids || []).includes(pid));
  if (!mySide) return "패";
  const expected = mySide.side_number === 1 ? "side_a" : "side_b";
  return match.winner_side === expected ? "승" : "패";
}

interface RoundEntry {
  rn: number;
  timeStr: string;
  info: {
    type?: string;
    court?: string | null;
    partner: string;
    opponents: string;
    match: MatchLike;
  } | null;
}

function ParticipantSchedule({
  pid,
  rounds,
}: {
  pid: string;
  rounds: RoundEntry[];
}) {
  return (
    <div class="stack">
      {rounds.map(({ rn, timeStr, info }) => {
        if (info) {
          const typeLabel = TYPE_LABELS[info.type || ""] || "경기";
          const result = resolveResult(info.match, pid);
          return (
            <div key={rn} class="schedule-row">
              <span class="schedule-row__round">R{rn}</span>
              <span class="schedule-row__time">{timeStr}</span>
              <div class="schedule-row__info">
                <span class="schedule-row__type">{typeLabel}</span>
                {info.court && <span class="schedule-row__court">코트 {info.court}</span>}
                <span class="schedule-row__detail">파트너: {info.partner || "-"}</span>
                <span class="schedule-row__detail">상대: {info.opponents || "-"}</span>
                {result && <span class="schedule-row__result">{result}</span>}
              </div>
            </div>
          );
        }
        return (
          <div key={rn} class="schedule-row schedule-row--rest">
            <span class="schedule-row__round">R{rn}</span>
            <span class="schedule-row__time">{timeStr}</span>
            <div class="schedule-row__info">
              <span class="schedule-row__type">휴식</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function buildParticipantScheduleView(
  p: ParticipantLike,
  matches: MatchLike[],
  roundMap: Record<number, RoundLike>,
): HTMLElement | null {
  const pid = p.event_participant_id;
  const byRound = new Map<number, RoundEntry["info"]>();
  for (const m of matches) {
    const rn = m.round_number || 0;
    const isPlaying = (m.player_ids || []).includes(pid);
    if (!isPlaying) continue;
    const side = (m.sides || []).find((s) => (s.player_ids || []).includes(pid));
    const partner = side
      ? (side.players || [])
        .filter((pl) => (pl.event_participant_id || pl.participant?.event_participant_id) !== pid)
        .map((pl) => matchPlayerName(pl))
        .join(", ")
      : "";
    const opponents = (m.sides || [])
      .filter((s) => s !== side)
      .flatMap((s) => (s.players || []).map((pl) => matchPlayerName(pl)))
      .join(", ");
    byRound.set(rn, { type: m.match_type, court: m.court_label, partner, opponents, match: m });
  }

  const allRounds = [...new Set(matches.map((m) => m.round_number || 0))].sort((a, b) => a - b);
  if (allRounds.length === 0) return null;

  const rounds: RoundEntry[] = allRounds.map((rn) => {
    const rd = roundMap[rn];
    const timeStr = rd
      ? `${formatTimeShort(rd.scheduled_start_at)}~${formatTimeShort(rd.scheduled_end_at)}`
      : "";
    return { rn, timeStr, info: byRound.get(rn) || null };
  });

  return mountPreact(<ParticipantSchedule pid={pid} rounds={rounds} />);
}
